import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Smartphone, QrCode, Copy, CheckCircle, XCircle, RefreshCw, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const PontoDevicesPage = () => {
  const [tab, setTab] = useState('codes');
  const [codes, setCodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);

  const loadCodes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/ponto-devices/codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Erro ao carregar códigos');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/ponto-devices/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Erro ao carregar dispositivos');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmpresas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpresas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('Erro ao carregar empresas');
    }
  }, []);

  useEffect(() => {
    loadEmpresas();
  }, [loadEmpresas]);

  useEffect(() => {
    if (tab === 'codes') loadCodes();
    else loadDevices();
  }, [tab, loadCodes, loadDevices]);

  const handleGenerateCode = async () => {
    if (!selectedCompanies.length) {
      toast.error('Selecione ao menos uma empresa');
      return;
    }
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/ponto-devices/generate-code`, {
        companies: selectedCompanies,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGeneratedCode(r.data);
      toast.success('Código gerado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar código');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleDevice = async (deviceId) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/api/ponto-devices/devices/${deviceId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(prev => prev.map(d =>
        d.device_id === deviceId ? { ...d, is_active: r.data.is_active } : d
      ));
      toast.success(r.data.message);
    } catch (e) {
      toast.error('Erro ao alterar dispositivo');
    }
  };

  const getEmpresaNome = (id) => {
    const e = empresas.find(e => e.id === id);
    return e?.name || e?.razao_social || id;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispositivos de Ponto</h1>
          <p className="text-sm text-gray-500">
            Gerencie códigos de registro e dispositivos autorizados
          </p>
        </div>
        <Button onClick={() => { setSelectedCompanies([]); setGeneratedCode(null); setShowGenerateModal(true); }}>
          <QrCode className="h-4 w-4 mr-2" />
          Gerar Código
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="codes">Códigos</TabsTrigger>
          <TabsTrigger value="devices">Dispositivos</TabsTrigger>
        </TabsList>

        <TabsContent value="codes" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Empresas</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Usado por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : codes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Nenhum código gerado
                      </TableCell>
                    </TableRow>
                  ) : codes.map(c => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{c.code}</code>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(c.code)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.companies?.map(id => (
                          <Badge key={id} variant="outline" className="mr-1">{getEmpresaNome(id)}</Badge>
                        ))}
                      </TableCell>
                      <TableCell>
                        {c.is_used ? (
                          <Badge className="bg-gray-100 text-gray-600">Usado</Badge>
                        ) : isExpired(c.expires_at) ? (
                          <Badge className="bg-red-100 text-red-600">Expirado</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-600">Disponível</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(c.created_at).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(c.expires_at).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {c.used_by_device_id ? c.used_by_device_id.slice(0, 8) + '...' : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dispositivo</TableHead>
                    <TableHead>Empresas</TableHead>
                    <TableHead>Dispositivo</TableHead>
                    <TableHead>Registrado em</TableHead>
                    <TableHead>Último acesso</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : devices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Nenhum dispositivo registrado
                      </TableCell>
                    </TableRow>
                  ) : devices.map(d => (
                    <TableRow key={d.device_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-4 w-4 text-gray-500" />
                          <code className="text-xs">{d.device_id.slice(0, 8)}...</code>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {d.companies?.map(id => (
                          <Badge key={id} variant="outline" className="mr-1">{getEmpresaNome(id)}</Badge>
                        ))}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {d.device_info?.modelo || '-'} / {d.device_info?.os || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {d.registered_at ? new Date(d.registered_at).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {d.last_access_at ? new Date(d.last_access_at).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell>
                        {d.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-600">Ativo</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-600">Desativado</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={d.is_active ? 'destructive' : 'outline'}
                          size="sm"
                          onClick={() => handleToggleDevice(d.device_id)}
                        >
                          {d.is_active ? 'Desativar' : 'Ativar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerar Código de Registro</DialogTitle>
            <DialogDescription>
              O código permite registrar um dispositivo para bater ponto.
              Cada código pode ser usado uma única vez e expira em 7 dias.
            </DialogDescription>
          </DialogHeader>

          {generatedCode ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <Label className="text-xs text-blue-600 mb-2 block">Código Gerado</Label>
                <code className="text-3xl font-bold font-mono tracking-widest text-blue-800 select-all">
                  {generatedCode.code}
                </code>
                <div className="flex justify-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(generatedCode.code)}>
                    <Copy className="h-4 w-4 mr-1" /> Copiar
                  </Button>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                <p><strong>Empresas:</strong> {generatedCode.companies?.map(id => getEmpresaNome(id)).join(', ')}</p>
                <p><strong>Expira em:</strong> {new Date(generatedCode.expires_at).toLocaleString('pt-BR')}</p>
              </div>
              <Button className="w-full" onClick={() => { setGeneratedCode(null); setShowGenerateModal(false); }}>
                Fechar
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Empresas autorizadas *</Label>
                <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-3">
                  {empresas.length === 0 ? (
                    <p className="text-sm text-gray-500">Carregando empresas...</p>
                  ) : (
                    empresas.map(emp => {
                      const isChecked = selectedCompanies.includes(emp.id);
                      return (
                        <label key={emp.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCompanies(prev => [...prev, emp.id]);
                              } else {
                                setSelectedCompanies(prev => prev.filter(id => id !== emp.id));
                              }
                            }}
                          />
                          <span className="text-sm">{emp.nome || emp.razao_social}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              <Button onClick={handleGenerateCode} className="w-full" disabled={generating || !selectedCompanies.length}>
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <QrCode className="h-4 w-4 mr-2" />}
                Gerar Código
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PontoDevicesPage;
